/**
 * Hook to trigger login-level preloading on Dashboard mount
 * Shows unobtrusive progress toast during indexing
 */

import { useEffect } from 'react';
import { loginLevelIndexer } from '@/services/preload/LoginLevelIndexer';
import { FEATURES } from '@/config/features';

/**
 * Non-blocking login-level preload hook
 * Triggers silently in background, never blocks UI
 */
export const useLoginPreload = (userId: string | undefined) => {
  useEffect(() => {
    if (!userId || !FEATURES.ENABLE_LOGIN_PRELOAD) return;

    // Delay start by 5 seconds to let dashboard render first - Phase 3 optimization
    const timer = setTimeout(async () => {
      try {
        console.log('🚀 [useLoginPreload] Starting background preload...');
        await loginLevelIndexer.indexUserLearningData(userId);
        console.log('✅ [useLoginPreload] Preload complete');
      } catch (error) {
        // Fail completely silently - preloading is optional
        console.warn('[useLoginPreload] Preload failed (non-critical):', error);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [userId]);

  // Don't expose loading state - preload is invisible to user
  return {
    isPreloading: false,
    preloadComplete: false,
  };
};
